import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { ClinicWithDistance } from '@features/clinics/types/clinic';
import type { AccountStatus } from '@features/coverage/types/clientClinic';
import './ClinicMatrixGrid.css';

interface ClinicMatrixGridProps {
  clinics: ClinicWithDistance[];
  selectedClinicId?: number;
  onClinicSelect: (clinicId: number) => void;
  emptyMessage?: string;
  showRelationshipStatus?: boolean;
  relationshipStatuses?: Map<number, AccountStatus>;
}

type SortDirection = 'asc' | 'desc' | null;
type SortConfig = { key: 'name' | 'distanceMiles'; direction: SortDirection };

function StatusBadge({ status }: Readonly<{ status: AccountStatus }>) {
  const className = `status-badge status-${status.toLowerCase()}`;
  return <span className={className}>{status}</span>;
}

export default function ClinicMatrixGrid({
  clinics,
  selectedClinicId,
  onClinicSelect,
  emptyMessage,
  showRelationshipStatus = false,
  relationshipStatuses = new Map(),
}: Readonly<ClinicMatrixGridProps>) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'distanceMiles',
    direction: 'asc',
  });

  const handleSort = (key: 'name' | 'distanceMiles') => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      if (prev.direction === 'desc') return { key: 'distanceMiles', direction: 'asc' };
      return { key, direction: null };
    });
  };

  const sortedClinics = useMemo(() => {
    if (!sortConfig.direction) return clinics;

    return [...clinics].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      } else {
        // Numeric sort; null distance (ungeocoded clinic) always sorts last
        const aNum = typeof aVal === 'number' ? aVal : Number.POSITIVE_INFINITY;
        const bNum = typeof bVal === 'number' ? bVal : Number.POSITIVE_INFINITY;
        comparison = aNum - bNum;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [clinics, sortConfig]);

  const columnCount = showRelationshipStatus ? 4 : 3;

  const SortableHeader = ({
    label,
    sortKey,
  }: Readonly<{
    label: string;
    sortKey: 'name' | 'distanceMiles';
  }>) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th onClick={() => handleSort(sortKey)} className="sortable-header">
        <div className="th-sort-content">
          <span>{label}</span>
          <span className={`sort-chevrons ${isActive ? 'active' : ''}`}>
            <ChevronUp
              size={12}
              className={`sort-icon ${isActive && sortConfig.direction === 'asc' ? 'active' : ''}`}
            />
            <ChevronDown
              size={12}
              className={`sort-icon ${isActive && sortConfig.direction === 'desc' ? 'active' : ''}`}
            />
          </span>
        </div>
      </th>
    );
  };

  return (
    <div className="clinic-matrix-grid">
      <table className="data-table">
        <thead>
          <tr>
            <SortableHeader label="Clinic Name" sortKey="name" />
            <th>Phone</th>
            <SortableHeader label="Distance" sortKey="distanceMiles" />
            {showRelationshipStatus && <th>Status</th>}
          </tr>
        </thead>
        <tbody>
          {sortedClinics.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="empty-message">
                {emptyMessage ?? 'No clinics found'}
              </td>
            </tr>
          ) : (
            sortedClinics.map((clinic) => {
              const status = relationshipStatuses.get(clinic.id);
              return (
                <tr
                  key={clinic.id}
                  className={selectedClinicId === clinic.id ? 'selected' : ''}
                  onClick={() => onClinicSelect(clinic.id)}
                >
                  <td className="clinic-name-cell">
                    <div className="clinic-name">{clinic.name}</div>
                    <div className="clinic-location">
                      {clinic.city}, {clinic.state}
                    </div>
                  </td>
                  <td className="phone-cell">{clinic.phone}</td>
                  <td className="distance-cell">
                    {clinic.distanceMiles != null ? `${clinic.distanceMiles.toFixed(1)} mi` : '—'}
                  </td>
                  {showRelationshipStatus && (
                    <td className="status-cell">
                      {status ? (
                        <StatusBadge status={status} />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
